import { useEffect, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { LetterWheel } from "../components/LetterWheel";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface SlotView {
  len: number;
  x: number;
  y: number;
  d: 0 | 1;
  /** null while unfound. */
  word: string | null;
}

interface View {
  letters: string[];
  cols: number;
  rows: number;
  slots: SlotView[];
  hintCells: { x: number; y: number; ch: string }[];
  found: number;
  total: number;
  bonus: string[];
  misses: number;
  hints: number;
}

interface GuessExtra {
  accepted?: boolean;
  bonus?: boolean;
  word?: string;
  hint?: { x: number; y: number; ch: string };
}

const COPY = {
  bad_word: "Тільки українські літери",
  bad_length: "Мінімум 3 літери",
  bad_letters: "Таких літер немає на колесі",
  already_guessed: "Це слово вже було",
  no_hint: "Нема що підказувати",
};

function Grid({ v }: { v: View }) {
  const cells = new Map<string, string | null>();
  for (const s of v.slots) {
    for (let i = 0; i < s.len; i++) {
      const k = `${s.x + (s.d === 0 ? i : 0)},${s.y + (s.d === 1 ? i : 0)}`;
      const ch = s.word?.[i] ?? null;
      if (ch || !cells.has(k)) cells.set(k, ch);
    }
  }
  for (const h of v.hintCells) {
    const k = `${h.x},${h.y}`;
    if (!cells.get(k)) cells.set(k, h.ch);
  }
  const px = Math.min(40, Math.floor(328 / v.cols), Math.floor(280 / v.rows));
  const gap = 3;
  return (
    <div
      className="relative mx-auto"
      style={{ width: v.cols * px + (v.cols - 1) * gap, height: v.rows * px + (v.rows - 1) * gap }}
    >
      {[...cells.entries()].map(([k, ch]) => {
        const [x, y] = k.split(",").map(Number) as [number, number];
        return (
          <div
            key={k}
            className={`absolute flex items-center justify-center rounded-md font-extrabold uppercase ${
              // rgba, not bg-tgsection: pure-black themes have secondary_bg ≈ bg,
              // and the empty grid disappears entirely.
              ch ? "bg-tgbutton text-tgbuttontext" : "bg-[rgba(128,128,128,0.22)]"
            }`}
            style={{ left: x * (px + gap), top: y * (px + gap), width: px, height: px, fontSize: px * 0.55 }}
          >
            {ch ?? ""}
          </div>
        );
      })}
    </div>
  );
}

export function Slovokolo({ date }: { date?: string | undefined }) {
  const g = useGame<View>("slovokolo", COPY, date);
  const [wheel, setWheel] = useState<string[] | null>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  useEffect(() => {
    if (wheel === null && state) setWheel(state.view.letters);
  }, [wheel, state]);

  const shuffle = () => {
    haptic.tap();
    setWheel((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      for (let i = next.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j] as string, next[i] as string];
      }
      return next;
    });
  };

  const submit = async (word: string) => {
    if (finished || g.busy) return;
    const next = await g.run(() => api.guess<View>("slovokolo", word, date));
    if (next) {
      const extra = next.extra as GuessExtra | undefined;
      if (extra?.bonus) g.showToast(`⭐ бонус: ${(extra.word ?? word).toUpperCase()}`);
      else if (extra?.accepted === false) g.showToast("Нема такого слова");
    }
  };

  const hint = async () => {
    if (finished || g.busy) return;
    await g.run(() => api.guess<View>("slovokolo", "hint", date));
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const score = v.misses + v.hints;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="СЛОВОКОЛО" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mt-1 text-center text-xs text-tghint">
          {v.found}/{v.total} слів
          {v.bonus.length > 0 ? ` · ⭐${v.bonus.length}` : ""}
          {v.misses > 0 ? ` · ${v.misses} пром.` : ""}
          {v.hints > 0 ? ` · ${v.hints} 💡` : ""}
        </div>
        <div className="mt-3">
          <Grid v={v} />
        </div>
      </div>

      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        {!finished && wheel && (
          <div className="relative">
            <LetterWheel key={wheel.join("")} letters={wheel} disabled={g.busy} minLen={3} onSubmit={(w) => void submit(w)} />
            <button
              type="button"
              onClick={shuffle}
              className="absolute left-4 top-10 h-11 w-11 rounded-full bg-[rgba(128,128,128,0.18)] text-lg active:opacity-70"
            >
              🔀
            </button>
            <button
              type="button"
              onClick={() => void hint()}
              disabled={g.busy}
              className="absolute right-4 top-10 h-11 w-11 rounded-full bg-[rgba(128,128,128,0.18)] text-lg active:opacity-70 disabled:opacity-40"
              title="підказка (+1 до рахунку)"
            >
              💡
            </button>
          </div>
        )}
        {!finished && (
          <div className="mt-2 flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("slovokolo", date))} />
          </div>
        )}
        {finished && v.bonus.length > 0 && (
          <div className="px-6 pb-2 text-center text-xs text-tghint">
            ⭐ бонуси: {v.bonus.map((b) => b.toUpperCase()).join(", ")}
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Коло пройдено!" : "Здався"}
          subtitle={
            won
              ? `${score === 0 ? "чисто, без підказок" : `${v.misses} пром. · ${v.hints} 💡`}${v.bonus.length ? ` · ⭐${v.bonus.length}` : ""} · стрік ${s.me.streak} 🔥`
              : `знайшов ${v.found}/${v.total} слів · стрік згорів`
          }
          scoreLabel="avg пром."
          formatScore={(n) => `${n ?? "–"}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
